<template>
  <loading-page
    v-if="fetchingBuckets"
    full
  />
  <div
    v-else
    class="lens-wrapper"
  >
    <div class="d-flex flex-wrap mb-2">
      <button-loading
        variant="bg-primary"
        class="mr-1"
        :spinning="gettingLens"
        @click="onUpdate()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Update
      </button-loading>
      <v-select
        v-if="bucketOptions.length > 0"
        v-model="selectedBucket"
        label="label"
        :options="bucketOptions"
        class="flex-fill distro-multi-select mr-md-1"
        placeholder="All buckets shown. You can filter buckets from here"
      />
      <custom-select
        id="interval"
        v-model="interval"
        class="interval border-default border"
        :options="intervalOptions"
      />
    </div>

    <div class="d-lg-flex">
      <div class="col-12 col-lg-5 p-0 pr-lg-1 mb-2 mb-lg-0">
        <div class="d-flex flex-wrap storage-statistics chart-wrapper">
          <statistic
            class="p-1 col-6"
            :title="lens ? formatBytes(lens.totalStorage) : '-'"
            subtitle="Total Storage"
            icon="ArrowRight"
          />
          <statistic
            class="p-1 col-6"
            :title="
              lens && lens.activeBuckets
                ? lens.activeBuckets.toLocaleString()
                : '-'
            "
            subtitle="Active Buckets"
            icon="ArrowRight"
          />
          <statistic
            class="p-1 col-6"
            :title="
              lens && lens.objectCount ? lens.objectCount.toLocaleString() : '-'
            "
            subtitle="Object Count"
            icon="ArrowRight"
          />
          <statistic
            class="p-1 col-6"
            :title="lens ? formatBytes(lens.averageObjectSize) : '-'"
            subtitle="Average Object Size"
            icon="ArrowRight"
          />
        </div>
      </div>
      <div class="chart-wrapper scrollable col-12 col-lg-7 p-0">
        <div class="scrollable-table">
          <b-table
            class="buckets-table"
            small
            :fields="bucketFields"
            :items="topBuckets"
            responsive="sm"
            borderless
            show-empty
            tbody-tr-class="customSmallTable"
          />
        </div>
      </div>
    </div>

    <loading-page v-if="!initialized || !selectedBucket" />
    <time-chart
      v-else
      :interval="interval"
      :bucket="selectedBucket"
      :update-index="updateIndex"
    />

    <loading-page v-if="!initialized || !selectedBucket" />
    <rgw-chart
      v-else
      :interval="interval"
      :bucket="selectedBucket"
      :update-index="updateIndex"
    />
  </div>
</template>
<script>
import vSelect from 'vue-select'
import Statistic from '@/components/statistics/Statistic.vue'
import TimeChart from './charts/TimeChart.vue'
import RgwChart from './charts/RgwChart.vue'

export default {
  name: 'StorageLens',
  components: {
    Statistic,
    vSelect,
    TimeChart,
    RgwChart,
  },
  data() {
    return {
      selectedBucket: null,
      interval: 7,
      intervalOptions: [
        { value: 1, text: '1 day' },
        { value: 7, text: '1 week' },
        { value: 14, text: '2 weeks' },
        { value: 30, text: '1 month' },
        { value: 60, text: '2 months' },
      ],
      bucketFields: [
        'bucket',
        {
          key: 'totalStorage',
          formatter: value => this.formatBytes(value),
        },
        {
          key: 'percentageOfTotal',
          label: 'Percentage',
          formatter: value => `${value}%`,
        },
        {
          key: 'objectCount',
          formatter: value => (value ? value.toLocaleString() : 0),
        },
        {
          key: 'averageObjectSize',
          formatter: value => this.formatBytes(value),
        },
      ],
      updateIndex: 0,
      initialized: false,
    }
  },
  computed: {
    fetchingBuckets() {
      return this.$store.state.buckets.fetchingBuckets
    },
    buckets() {
      return this.$store.state.buckets.buckets
    },
    lens() {
      return this.$store.state.storageLensV2.lens
    },
    gettingLens() {
      return this.$store.state.storageLensV2.gettingLens
    },
    topBuckets() {
      return this.lens?.topBuckets || []
    },
    bucketOptions() {
      return this.buckets.map(bucket => ({
        value: bucket.name,
        label: bucket.name,
      }))
    },
    accountId() {
      return this.$store.state.externalAccount.accountId
    },
  },
  created() {
    this.$store
      .dispatch('accountBuckets/fetchBuckets', this.accountId)
      .then(() => {
        this.setDefaultBucket()
        this.getLens()
      })
      .catch(error => {
        const { status } = error.response || null
        if (status === 424) {
          this.$store.commit('buckets/SET_SHOW_INTRO', true)
          return
        }
        if (status === 426 || status === 428) {
          this.$store.commit('buckets/SET_SERVICE_UNAVAILABLE', status === 428)
          return
        }
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching buckets.')
      })
  },
  methods: {
    getLens() {
      this.$store.dispatch('storageLensV2/getLens').catch(error => {
        this.errorToast(
          error.response?.data?.errors[0]
            || 'Something went wrong while fetching lens data.',
        )
      })
    },
    setDefaultBucket() {
      this.selectedBucket = {
        value: this.buckets[0].name,
        label: this.buckets[0].name,
      }
      this.initialized = true
    },
    onUpdate() {
      this.getLens()
      this.updateIndex++
    },
  },
}
</script>
<style lang="scss" scoped>
.lens-wrapper {
  margin: auto;
  max-width: 1920px;
  .interval {
    max-width: 115px;
  }

  .scrollable {
    overflow-y: auto;
    .scrollable-table {
      .buckets-table::v-deep {
        tr {
          th {
            vertical-align: middle;
          }
          td,
          th {
            padding: 5px;
            &:first-child {
              padding-left: 10px;
            }
          }
          &.customSmallTable:first-child {
            padding-top: 10px;
          }
        }
      }
    }
  }

  .distro-multi-select {
    @media (max-width: 767.8px) {
      margin-top: 1rem;
      order: 2;
      width: 100%;
      min-height: 42px;
    }
  }
}

@media (min-width: 992px) {
  .scrollable {
    .scrollable-table {
      min-height: 100%;
      height: 0;
    }
  }

  .storage-statistics {
    min-height: 200px;
  }
}
</style>
